import React, { Component, Fragment } from "react"
import { observer } from "mobx-react"
import { Link } from "react-router-dom"
import { Button, Icon, Message, Modal, Divider } from "semantic-ui-react"
import { Col, Row, FormField, FormInput, Checkbox, Form } from "elemental-react16"
import shop from "shop/shop"
import track from "shop/track"
import moment from "moment"
import _ from "lodash"
import swal from "sweetalert"
import queryString from "qs"
import ProductTable from "components/Product/ProductTable"
import Price from "components/Price/Price"
import Spinner from "components/Spinner/Spinner"
import SuccessTable from "components/Product/SuccessTable"
import productStore from "stores/productStore"
import DeliveryDatePicker from "components/Date/DeliveryDatePicker"
import "style/CheckoutPage.scss"
import "style/DeliveryPage.scss"

window.moment = moment

// ── Visual helpers ──────────────────────────────────────────────────────────

const SectionCard = ( { children, style = {} } ) => (
  <div style={{
    background    : "#fff",
    border        : "1px solid #e8e8e8",
    borderRadius  : "8px",
    padding       : "28px",
    marginBottom  : "20px",
    boxShadow     : "0 1px 4px rgba(0,0,0,0.07)",
    ...style
  }}>
    {children}
  </div>
)

const StepHeader = ( { number, title, done } ) => (
  <div style={{
    display      : "flex",
    alignItems   : "center",
    marginBottom : "20px",
    paddingBottom: "14px",
    borderBottom : "2px solid #f0f0f0"
  }}>
    <div style={{
      width          : "34px",
      height         : "34px",
      borderRadius   : "50%",
      background     : done ? "#21ba45" : "#1b1c1d",
      color          : "#fff",
      display        : "flex",
      alignItems     : "center",
      justifyContent : "center",
      fontWeight     : "700",
      fontSize       : "0.95rem",
      marginRight    : "14px",
      flexShrink     : 0,
      transition     : "background 0.2s"
    }}>
      {done ? <Icon name="check" style={{ margin: 0, fontSize: "0.8rem" }}/> : number}
    </div>
    <h2 style={{ margin: 0, fontSize: "1.15rem", fontWeight: "600" }}>{title}</h2>
  </div>
)

// ── Address box with address picker modal ────────────────────────────────────

const AddressBox = ( { title, address, fixed, fallback, locations = [], locationSelected, onSelect } ) => {
  const [open, setOpen] = React.useState( false )
  return (
    <div style={{
      flex        : 1,
      border      : "1px solid #e8e8e8",
      borderRadius: "6px",
      padding     : "16px",
      minHeight   : "130px",
      background  : "#fafafa"
    }}>
      <div style={{
        fontWeight    : "600",
        color         : "#888",
        fontSize      : "0.78rem",
        textTransform : "uppercase",
        letterSpacing : "0.06em",
        marginBottom  : "10px"
      }}>
        {title}
      </div>

      {address && (
        <div style={{ lineHeight: "1.6", fontSize: "0.92rem" }}>
          <strong>{address.title || `${address.name1 || ""} ${address.name2 || ""}`.trim() || address.company}</strong><br/>
          {address.street && <span>{address.street}<br/></span>}
          {(address.pcode || address.city) && <span>{address.pcode} {address.city}<br/></span>}
          {address.country && <span>{address.country}<br/></span>}
          {address.email && <span style={{ color: "#666" }}>{address.email}<br/></span>}
          {address.phone && <span style={{ color: "#666" }}>{address.phone}</span>}
        </div>
      )}

      {!address && fallback && (
        <div style={{ color: "#666", fontSize: "0.9rem" }}>{fallback}</div>
      )}

      {!fixed && locations.length > 0 && (
        <Fragment>
          <Button basic size="mini" style={{ marginTop: "12px" }} onClick={() => setOpen( true )}>
            <Icon name="edit outline"/> {translate( "ändern" )}
          </Button>

          <Modal open={open} onClose={() => setOpen( false )} size="large">
            <Modal.Header>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{translate( "Lieferadresse auswählen" )}</span>
                <Button basic circular icon="times" onClick={() => setOpen( false )}/>
              </div>
            </Modal.Header>
            <Modal.Content>
              {/* Option: zurück zur Rechnungsadresse */}
              <div
                onClick={() => onSelect( null )}
                style={{
                  border      : !locationSelected ? "2px solid #2185d0" : "1px solid #e0e0e0",
                  borderRadius: "6px",
                  padding     : "14px",
                  cursor      : "pointer",
                  background  : !locationSelected ? "#f0f7ff" : "#fff",
                  marginBottom: "16px",
                  display     : "flex",
                  alignItems  : "center",
                  gap         : "10px"
                }}
              >
                {!locationSelected && <Icon name="check circle" color="blue" style={{ margin: 0 }}/>}
                <div>
                  <div style={{ fontWeight: "600" }}>{translate( "Rechnungsadresse verwenden" )}</div>
                  <div style={{ fontSize: "0.82rem", color: "#888" }}>{translate( "Lieferung an die Rechnungsadresse" )}</div>
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                {locations.map( ( loc, i ) => {
                  const sel = locationSelected === loc.mapid
                  return (
                    <div
                      key={i}
                      onClick={() => onSelect( loc )}
                      style={{
                        flex        : "0 0 calc(33.33% - 8px)",
                        border      : sel ? "2px solid #2185d0" : "1px solid #e0e0e0",
                        borderRadius: "6px",
                        padding     : "14px",
                        cursor      : "pointer",
                        background  : sel ? "#f0f7ff" : "#fff",
                        transition  : "all 0.15s"
                      }}
                    >
                      {sel && <Icon name="check circle" color="blue" style={{ marginBottom: "4px" }}/>}
                      <div style={{ fontWeight: "600" }}>{loc.name1} {loc.name2}</div>
                      <div style={{ fontSize: "0.88rem", color: "#555" }}>
                        {loc.street}<br/>
                        {loc.pcode} {loc.city}
                      </div>
                    </div>
                  )
                } )}
              </div>
            </Modal.Content>
            <Modal.Actions>
              <Button primary onClick={() => setOpen( false )}>
                <Icon name="check"/> {translate( "Adresse bestätigen" )}
              </Button>
            </Modal.Actions>
          </Modal>
        </Fragment>
      )}
    </div>
  )
}

// ── Success view ─────────────────────────────────────────────────────────────

const SuccessView = ( { bill, byId } ) => {
  if ( !bill ) return null
  const order  = bill
  const isOffer = order.extras[ "ts-ordertype" ] === "offer"

  return (
    <div className="ui container mw9 ph3 center">
      {/* Success banner */}
      <div style={{ textAlign: "center", padding: "56px 0 32px" }}>
        <div style={{
          width          : "72px",
          height         : "72px",
          borderRadius   : "50%",
          background     : "#21ba45",
          color          : "#fff",
          display        : "flex",
          alignItems     : "center",
          justifyContent : "center",
          margin         : "0 auto 20px",
          boxShadow      : "0 4px 16px rgba(33,186,69,0.3)"
        }}>
          <Icon name="check" style={{ margin: 0, fontSize: "2rem" }}/>
        </div>
        <h1 style={{ marginBottom: "8px" }}>
          {translate( "confirmation.title" )} {isOffer && translate( "Ihrer Anfrage" )}
        </h1>
        {shop.session.contact && (
          <p style={{ color: "#666", fontSize: "1.05rem" }}>
            {isOffer
              ? translate( "confirmation.thanks_request_anonymous" )
              : translate( "confirmation.thanks_order_anonymous" )}
          </p>
        )}
      </div>

      <div style={{ maxWidth: "820px", margin: "0 auto 48px" }}>
        <SectionCard>
          <h4 style={{ marginTop: 0 }}>{translate( "your data" )}</h4>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <colgroup><col width="35%"/><col width="65%"/></colgroup>
            <tbody>
              {shop.config.checkoutFeatures && shop.config.checkoutFeatures.orderNumber && (
                <tr>
                  <td style={{ padding: "6px 0", color: "#888" }}>{translate( "Order Id" )}</td>
                  <td style={{ fontWeight: "600" }}>{order.handid}</td>
                </tr>
              )}
              {order.extras.manual_order_id && (
                <tr>
                  <td style={{ padding: "6px 0", color: "#888" }}>{translate( "manual_order_id" )}</td>
                  <td>{order.extras.manual_order_id}</td>
                </tr>
              )}
              <tr>
                <td style={{ padding: "6px 0", color: "#888" }}>{translate( "Kundennummer" )}</td>
                <td>{order.customerid}</td>
              </tr>
              <tr>
                <td style={{ padding: "6px 0", color: "#888" }}>{translate( "E-Mail" )}</td>
                <td>{order.customer.email || order.contact.email || "-"}</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>

        <SectionCard>
          <Row>
            <Col sm="1/2">
              <h4>{translate( "Rechnungsadresse" )}</h4>
              <p style={{ lineHeight: "1.6" }}>
                {order.bill.name1} {order.bill.name2} {order.bill.name3}<br/>
                {order.bill.street}<br/>
                {order.bill.pcode} {order.bill.city}<br/>
                {order.bill.country}
              </p>
            </Col>
            <Col sm="1/2">
              <h4>{translate( "Lieferadresse" )}</h4>
              <p style={{ lineHeight: "1.6" }}>
                {order.deliver.name1
                  ? <Fragment>{order.deliver.name1} {order.deliver.name2} {order.deliver.name3}<br/>{order.deliver.street}<br/>{order.deliver.pcode} {order.deliver.city}</Fragment>
                  : <Fragment>{order.bill.name1} {order.bill.name2}<br/>{order.bill.street}<br/>{order.bill.pcode} {order.bill.city}</Fragment>
                }
              </p>
            </Col>
          </Row>
        </SectionCard>

        <SectionCard>
          <h4 style={{ marginTop: 0 }}>
            {isOffer ? translate( "Ihre Anfrage" ) : translate( "Ihre Bestellung" )}
          </h4>
          <SuccessTable
            showTax={order.extras.payment}
            positions={order.positions}
            byId={byId}
            netto={order.netto}
            currency={order.currency}
          />
          {order.comment && (
            <div style={{ marginTop: "16px" }}>
              <strong>{translate( "your comment" )}</strong>
              <p>{order.comment}</p>
            </div>
          )}
          {!order.extras.payment && (
            <div style={{ marginTop: "16px", padding: "14px", background: "#f5f5f5", borderRadius: "6px" }}>
              <p style={{ margin: 0, fontWeight: "600" }}>{translate( "confirmation.notice" )}</p>
              <p style={{ margin: "4px 0 0", color: "#555" }}>{translate( "confirmation.notice2" )}</p>
            </div>
          )}
        </SectionCard>

        <div style={{ textAlign: "center", paddingBottom: "32px" }}>
          <Link to="/">
            <Button primary size="large">
              <Icon name="chevron left"/> {translate( "zurück zum Shop" )}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}

// ── Main OnePageCheckoutPage ──────────────────────────────────────────────────

@observer
export default class OnePageCheckoutPage extends Component {

  state = {
    products        : getProducts(),
    locations       : [],
    shortlist       : [],
    locationSelected: null,
    comment         : shop.cart.data.comment || "",
    manual_order_id : (shop.cart.data.extras && shop.cart.data.extras.manual_order_id) || "",
    agb             : false,
    privacy         : false,
    submitting      : false,
    submitted       : false,
    bill            : null,
    byId            : {},
  }

  componentDidMount() {
    shop.cart.hooks.changed.add( this.updateCart )
    track.checkout( { step: 1, products: shop.cart.products } )

    if ( shop.cart.data.deliveraddress && shop.cart.data.deliveraddress.mapid ) {
      this.setState( { locationSelected: shop.cart.data.deliveraddress.mapid } )
    }

    shop.fetch( "/shop/rest/data/customers/current/addresses/deliver" )
      .then( locations => {
        if ( locations.length < 1 ) return
        const shortlist = locations.slice( 0, 8 )
        this.setState( { locations, shortlist } )
      } )
  }

  componentWillUnmount() {
    shop.cart.hooks.changed.remove( this.updateCart )
  }

  updateCart = () => {
    this.setState( { products: getProducts() } )
    this.forceUpdate()
  }

  handleCartChange = ( { product } ) => {
    shop.cart.add( product )
  }

  handleLocationSelect = ( location ) => {
    if ( !location ) {
      this.setState( { locationSelected: null } )
      shop.cart.setDeliverAddress && shop.cart.setDeliverAddress( null )
      shop.cart.setAddress && shop.cart.setAddress( { location: null } )
      return
    }
    this.setState( { locationSelected: location.mapid } )
    shop.cart.setAddress( { location } )
  }

  handleCommentChange = ( e ) => {
    const comment = e.target.value
    this.setState( { comment } )
    shop.cart.setComment( { comment } )
  }

  handleOrderIdChange = ( e ) => {
    const manual_order_id = e.target.value
    this.setState( { manual_order_id } )
    shop.cart.setExtra( "manual_order_id", manual_order_id )
  }

  handleDateChange = ( date ) => {
    let deliverdate = moment( date )
    if ( deliverdate.isValid() ) {
      deliverdate = deliverdate.format( "DD.MM.YYYY" )
      this.setState( { deliverdate } )
      shop.cart.setDeliverdate( { deliverdate } )
    } else {
      shop.cart.setDeliverdate( { deliverdate: "" } )
      this.setState( { deliverdate: "" } )
    }
  }

  doSubmit = ( isOffer ) => {
    const { agb, privacy } = this.state
    if ( !agb )     return shop.toast( translate( "please confirm our general terms" ), "error" )
    if ( !privacy ) return shop.toast( translate( "please confirm our privacy information" ), "error" )

    this.setState( { submitting: true } )
    shop.cart.setExtra( "is_quotation_request", isOffer )

    const extras = {
      delivery_address_id : shop.cart.data.deliveraddress && shop.cart.data.deliveraddress.mapid,
      manual_order_id     : shop.cart.getExtra( "manual_order_id" ),
      is_quotation_request: isOffer,
      attachments         : shop.cart.getExtra( "attachments" ),
      "ts-ordertype"      : isOffer ? "offer" : null,
      print_receipt       : shop.cart.getExtra( "print_receipt" ),
    }
    _.map( shop.cart.data.extras, ( value, key ) => { if ( !extras[ key ] ) extras[ key ] = value } )

    shop.cart.submit( { extras } )
      .then( bill => {
        this.setState( { bill, submitting: false, submitted: true } )
        shop.cart.productsById.clear()
        shop.cart.clear()
        shop.cart.update()
        return Promise.all( bill.positions.map( p => productStore.get( p.article_id ) ) )
      } )
      .then( products => {
        const byId = {}
        products.forEach( p => { byId[ p.map_id ] = p } )
        this.setState( { byId } )
      } )
      .catch( error => {
        console.error( "Checkout Error:", error )
        this.setState( { submitting: false } )
        swal( translate( "Error" ), String( error ), "error" )
      } )
  }

  render() {
    const {
      products, locations, locationSelected, comment,
      agb, privacy, submitting, submitted, bill, byId
    } = this.state

    const { referer = "/" } = queryString.parse( location.search, { ignoreQueryPrefix: true } )
    const { checkoutType }  = this.props.match.params
    const isRequest         = !!checkoutType

    const billingAddress  = shop.session.value || {}
    const { deliveraddress, deliverdate } = shop.cart.data
    const manual_order_id = shop.cart.data.extras && shop.cart.data.extras.manual_order_id

    const tax      = shop.cart.sum / 100 * Number( shop.config.taxRate )
    const hasTax   = shop.config.taxRate
    const MIN_ORDER = shop.config.MIN_ORDER_VALUE || 0
    const canOrder  = agb && privacy && shop.cart.sum > MIN_ORDER

    const checkoutFeatures = shop.config.checkoutFeatures || {}
    const pluginFeatures   = shop.config.plugin || {}

    if ( submitted && bill ) return <SuccessView bill={bill} byId={byId}/>

    if ( submitting ) {
      return (
        <div style={{
          display       : "flex",
          justifyContent: "center",
          alignItems    : "center",
          minHeight     : "50vh",
          flexDirection : "column",
          gap           : "16px"
        }}>
          <Spinner size="md" type="inverted"/>
          <p style={{ color: "#666", fontSize: "1rem" }}>
            {translate( "Ihre Bestellung wird verarbeitet..." )}
          </p>
        </div>
      )
    }

    return (
      <div className="OnePageCheckout">

        {/* ─── Compact header ───────────────────────────────── */}
        <div style={{
          background  : "#fff",
          borderBottom: "1px solid #e8e8e8",
          padding     : "14px 0",
          marginBottom: "28px"
        }}>
          <div className="container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Link to={referer}>
              <img src={shop.config.logo} alt="" style={{ height: "38px", objectFit: "contain" }}/>
            </Link>
            <h1 style={{ margin: 0, fontSize: "1.25rem", fontWeight: "700", letterSpacing: "-0.01em" }}>
              {isRequest ? translate( "Anfrage" ) : translate( "Checkout" )}
            </h1>
            <Link to={referer} style={{ color: "#666", fontSize: "0.88rem" }}>
              <Icon name="arrow left"/> {translate( "Zurück zum Shop" )}
            </Link>
          </div>
        </div>

        {/* ─── Two-column layout ────────────────────────────── */}
        <div className="container" style={{ display: "flex", gap: "24px", alignItems: "flex-start", paddingBottom: "48px" }}>

          {/* Left: main content */}
          <div style={{ flex: "1 1 auto", minWidth: 0 }}>

            {/* ── SECTION 1: WARENKORB ─────────────────────── */}
            <SectionCard>
              <StepHeader number="1" title={translate( "cart.cart" )}/>
              <ProductTable cart={shop.cart} onChange={this.handleCartChange} products={products}/>

              {shop.cart.sum <= MIN_ORDER && MIN_ORDER > 0 && (
                <Message negative style={{ marginTop: "16px" }}>
                  <Icon name="info circle"/>
                  {translate( "minimum order value not reached" )}&nbsp;
                  <strong><Price>{MIN_ORDER}</Price></strong>
                </Message>
              )}
            </SectionCard>

            {/* ── SECTION 2: LIEFERUNG ─────────────────────── */}
            <SectionCard>
              <StepHeader number="2" title={translate( "2. Lieferung" )}/>

              {/* Address cards */}
              <div style={{ display: "flex", gap: "16px", marginBottom: "24px", flexWrap: "wrap" }}>
                <AddressBox
                  title={translate( "Rechnungsadresse" )}
                  address={billingAddress}
                  fixed
                />
                <AddressBox
                  title={translate( "Lieferadresse" )}
                  address={deliveraddress}
                  fallback={translate( "Entspricht der Rechnungsadresse" )}
                  locations={locations}
                  locationSelected={locationSelected}
                  onSelect={this.handleLocationSelect}
                />
              </div>

              {/* Comment */}
              <FormField label={translate( "comment" )} htmlFor="opc-comment">
                <FormInput
                  id="opc-comment"
                  placeholder={translate( "comment" )}
                  value={comment}
                  onChange={this.handleCommentChange}
                  multiline
                />
              </FormField>

              {/* Manual order ID */}
              {checkoutFeatures.manualOrderId && (
                <div style={{ maxWidth: "400px" }}>
                  <FormField label={translate( "order number" )}>
                    <FormInput
                      placeholder={translate( "order number" )}
                      value={manual_order_id}
                      onChange={this.handleOrderIdChange}
                    />
                  </FormField>
                </div>
              )}

              {/* Delivery date */}
              {(checkoutFeatures.deliverdate || pluginFeatures.deliverdate) && (
                <div style={{ maxWidth: "400px" }}>
                  <FormField label={translate( "delivery after" )}>
                    <DeliveryDatePicker date={deliverdate} onChange={this.handleDateChange}/>
                  </FormField>
                </div>
              )}

              {/* Back-office fields for Vertriebsaccounts */}
              {(shop.session.handId && !shop.config.hideAddtionalCheckoutMetaForPowerUsers) && (
                <div style={{ marginTop: "16px" }}>
                  <Divider/>
                  <h4 style={{ color: "#888" }}>{translate( "back office" )}</h4>
                  <div style={{ maxWidth: "400px" }}>
                    <FormField label={translate( "Belegausdruck/Innendienst" )}>
                      <FormInput
                        placeholder={translate( "Belegausdruck/Innendienst" )}
                        value={shop.cart.data.print_receipt}
                        onChange={e => shop.cart.setExtra( "print_receipt", e.target.value )}
                        multiline
                      />
                    </FormField>
                  </div>
                </div>
              )}
            </SectionCard>

            {/* ── SECTION 3: ABSCHLUSS ─────────────────────── */}
            <SectionCard>
              <StepHeader number="3" title={translate( "Abschluss" )}/>

              <Form type="horizontal">
                {/* AGB */}
                <FormField style={{ marginBottom: "20px" }}>
                  <div style={{ textAlign: "right", marginBottom: "6px" }}>
                    <Link target="_blank" to="/cms/agb" style={{ fontSize: "0.88rem" }}>
                      {translate( "AGB" )} ansehen
                    </Link>
                  </div>
                  <Checkbox
                    value={agb}
                    onChange={e => this.setState( { agb: e.target.checked } )}
                    label={translate( "I accept the general terms and conditions" )}
                  />
                </FormField>

                {/* Datenschutz */}
                <FormField>
                  <div style={{ textAlign: "right", marginBottom: "6px" }}>
                    <Link target="_blank" to="/cms/datenschutz" style={{ fontSize: "0.88rem" }}>
                      {translate( "Datenschutzerklärung" )} ansehen
                    </Link>
                  </div>
                  <Checkbox
                    value={privacy}
                    onChange={e => this.setState( { privacy: e.target.checked } )}
                    label={translate( "privacy_notice_notice" )}
                  />
                </FormField>
              </Form>

              <Divider/>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "4px" }}>
                <Link to={referer}>
                  <Button basic>{translate( "Zurück" )}</Button>
                </Link>
                {!isRequest && (
                  <Button
                    primary
                    disabled={!canOrder}
                    loading={submitting}
                    onClick={() => this.doSubmit( false )}
                    style={{ paddingLeft: "28px", paddingRight: "28px" }}
                  >
                    <Icon name="check"/> {translate( "order now" )}
                  </Button>
                )}
                {isRequest && (
                  <Button
                    primary
                    disabled={!agb || !privacy}
                    loading={submitting}
                    onClick={() => this.doSubmit( true )}
                    style={{ paddingLeft: "28px", paddingRight: "28px" }}
                  >
                    {translate( "send enquiry" )}
                  </Button>
                )}
              </div>
            </SectionCard>

          </div>{/* end left column */}

          {/* ─── Right: sticky order summary ──────────────── */}
          <div style={{ width: "300px", flexShrink: 0, position: "sticky", top: "24px" }}>
            <SectionCard>
              <h3 style={{ marginTop: 0, marginBottom: "16px", fontWeight: "700", fontSize: "1rem" }}>
                {translate( "Zusammenfassung" )}
              </h3>

              <div style={{ borderTop: "1px solid #eee", paddingTop: "14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", color: "#666", fontSize: "0.9rem" }}>
                  <span>{shop.cart.size} {translate( "positions" )}</span>
                  <Price>{shop.cart.sum}</Price>
                </div>

                {shop.session.handId && !!shop.cart.sumDiscount && (
                  <Fragment>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", color: "#21ba45", fontSize: "0.9rem" }}>
                      <span>{translate( "Discount" )}</span>
                      <span>- <Price>{shop.cart.sumDiscount}</Price></span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "0.9rem" }}>
                      <span>{translate( "nach Rabatt" )}</span>
                      <Price>{shop.cart.sum - shop.cart.sumDiscount}</Price>
                    </div>
                  </Fragment>
                )}

                {hasTax && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", color: "#888", fontSize: "0.85rem" }}>
                    <span>{translate( "zzgl. Steuern" )} ({shop.config.taxRate.replace( ".", "," )}%)</span>
                    <Price>{tax}</Price>
                  </div>
                )}

                <div style={{
                  display       : "flex",
                  justifyContent: "space-between",
                  fontWeight    : "700",
                  fontSize      : "1.1rem",
                  paddingTop    : "10px",
                  borderTop     : "1px solid #eee",
                  marginTop     : "6px"
                }}>
                  <span>{hasTax ? translate( "inkl. Steuern" ) : translate( "Gesamt" )}</span>
                  <Price>{hasTax ? tax + shop.cart.sum : shop.cart.sum}</Price>
                </div>
              </div>

              {(() => {
                const hasDeliverAddr = deliveraddress && deliveraddress.mapid
                const addr = hasDeliverAddr ? deliveraddress : billingAddress
                if ( !addr ) return null
                return (
                  <div style={{
                    marginTop   : "16px",
                    padding     : "12px",
                    background  : "#f4f7fb",
                    borderRadius: "6px",
                    fontSize    : "0.82rem",
                    lineHeight  : "1.5"
                  }}>
                    <div style={{ fontWeight: "600", color: "#666", marginBottom: "2px" }}>
                      <Icon name="truck" style={{ marginRight: "4px" }}/>{translate( "Lieferung an:" )}
                    </div>
                    <div>{addr.name1} {addr.name2}</div>
                    <div>{addr.street}</div>
                    <div>{addr.pcode} {addr.city}</div>
                    {!hasDeliverAddr && (
                      <div style={{ color: "#999", fontSize: "0.78rem", marginTop: "2px" }}>
                        ({translate( "Rechnungsadresse" )})
                      </div>
                    )}
                  </div>
                )
              })()}

              <div style={{ marginTop: "20px" }}>
                {!isRequest && (
                  <Button
                    primary fluid size="large"
                    disabled={!canOrder}
                    loading={submitting}
                    onClick={() => this.doSubmit( false )}
                  >
                    <Icon name="check"/> {translate( "order now" )}
                  </Button>
                )}
                {isRequest && (
                  <Button
                    primary fluid size="large"
                    disabled={!agb || !privacy}
                    loading={submitting}
                    onClick={() => this.doSubmit( true )}
                  >
                    {translate( "send enquiry" )}
                  </Button>
                )}

                {(!agb || !privacy) && (
                  <p style={{ fontSize: "0.78rem", color: "#aaa", textAlign: "center", marginTop: "8px", marginBottom: 0 }}>
                    <Icon name="info circle"/> {translate( "Bitte bestätigen Sie AGB und Datenschutz" )}
                  </p>
                )}

                {agb && privacy && shop.cart.sum <= MIN_ORDER && MIN_ORDER > 0 && (
                  <Message negative size="small" style={{ marginTop: "10px", fontSize: "0.82rem" }}>
                    <Icon name="info circle"/>
                    {translate( "minimum order value not reached" )}
                  </Message>
                )}
              </div>
            </SectionCard>

            {/* Trust signals */}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "4px 0" }}>
              {[
                { icon: "lock", text: translate( "Sichere Verbindung" ) },
                { icon: "shield alternate", text: translate( "Datenschutz garantiert" ) },
              ].map( ( item, i ) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", color: "#888", fontSize: "0.82rem" }}>
                  <Icon name={item.icon} style={{ margin: 0, color: "#21ba45" }}/>
                  <span>{item.text}</span>
                </div>
              ) )}
            </div>
          </div>

        </div>{/* end two-column */}
      </div>
    )
  }
}

const getProducts = () => shop.cart.map( product => product )



// WEBPACK FOOTER //
// ./src/pages/checkout/OnePageCheckoutPage.js